import { cloneDeep, debounce, get, isEqual } from 'lodash';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Calendar, Location, Unit } from 'sr-types/lib/search/v1/graphql';
import { FormProvider } from '../../form/FormContext';
import { useBrowserOrAccountLocale } from '../../hooks/useBrowserOrAccountLocale';
import { failedToastMessage } from '../../utils/commonUtils';
import { DEFAULT_SCHEDULE_FORM_WIDGET_VALUE, SCHEDULE_CALCULATION_DEBOUNCE_MS } from './constants';
import { useGetCalendars } from './data/useGetCalendars';
import { useGetDurationUnits } from './data/useGetDurationUnits';
import { ScheduleFormWidgetContext } from './ScheduleFormWidgetContext';
import {
    ScheduleFormWidgetCommonContextValue,
    ScheduleFormWidgetContextValue,
    ScheduleFormWidgetValue,
    ScheduleHierarchyFormWidgetValue,
    SupportedSchedulerDurationUnit
} from './types';
import { calculateNextScheduleHierarchyFormWidgetValue } from './utils/schedule-hierarchy-utils';
import {
    calculateNextStandAloneScheduleFormWidgetValue,
    isScheduleValueEqual
} from './utils/stand-alone-schedule-utils';

type ScheduleFormWidgetProviderCommonProps = PropsWithChildren<{
    disabled?: boolean;

    allowedDurationUnits?: SupportedSchedulerDurationUnit[];
    /**
     * Optional: Pre-fetched duration units data. If provided, skips the internal API call.
     * Used to avoid duplicate API calls when parent component already has units data.
     */
    durationUnits?: Unit[];
    allowedLocations?: Array<Location>;
    allowAdditionalLocations?: boolean;
    allowedCalendars?: Array<Calendar>;

    onCalculationStateChange?: (isCalculating: boolean) => void;

    nameFormFieldLabelToken?: string;
    specifyDurationFormFieldLabelToken?: string;
    scheduleHierarchyModeFormFieldLabelToken?: string;
    sequentialFormFieldLabelToken?: string;
}>;

export type ScheduleFormWidgetProviderProps =
    | ({
          mode: 'stand-alone-schedule';
          value: ScheduleFormWidgetValue;
          onChange: (value: ScheduleFormWidgetValue) => void;
      } & ScheduleFormWidgetProviderCommonProps)
    | ({
          mode: 'hierarchy-schedule';
          value: ScheduleHierarchyFormWidgetValue;
          onChange: (value: ScheduleHierarchyFormWidgetValue) => void;
      } & ScheduleFormWidgetProviderCommonProps);

export default function ScheduleFormWidgetProvider({
    mode,
    value,
    onChange,
    disabled = false,
    allowedDurationUnits,
    allowedLocations,
    allowAdditionalLocations,
    allowedCalendars,
    durationUnits: providedDurationUnits,
    onCalculationStateChange,
    nameFormFieldLabelToken,
    specifyDurationFormFieldLabelToken,
    scheduleHierarchyModeFormFieldLabelToken,
    sequentialFormFieldLabelToken,
    children
}: ScheduleFormWidgetProviderProps) {
    const locale = useBrowserOrAccountLocale();

    // Load data needed for form fields --------------------------------------------------------------------------------

    /**
     * This is done here, instead of within each form widget, to avoid multiple requests when multiple widgets
     * of the same type are used within the form (e.g., multiple duration unit widgets).
     *
     * This also gives better control over internal state initialization and allows delaying the enabling of form
     * widgets until all required data is loaded for predictable behavior.
     */

    const { durationUnitsData, isLoadingDurationUnitsData, errorLoadingDurationUnitsData } =
        useGetDurationUnits(providedDurationUnits);

    const {
        calendarsData,
        isLoadingCalendarsData: isLoadingCalendarOptions,
        errorLoadingCalendarsData: errorLoadingCalendarOptions
    } = useGetCalendars(allowedCalendars);

    const loading = isLoadingDurationUnitsData || isLoadingCalendarOptions;

    const error = useMemo(
        () => ({
            duration: {
                unit: errorLoadingDurationUnitsData?.message
            },
            calendar: errorLoadingCalendarOptions?.message
        }),
        [errorLoadingCalendarOptions?.message, errorLoadingDurationUnitsData?.message]
    );

    // Manage internal calculated state -------------------------------------------------------------------------------

    const [scheduleFormWidgetState, setScheduleFormWidgetState] = useState<ScheduleFormWidgetValue>(
        cloneDeep({
            ...DEFAULT_SCHEDULE_FORM_WIDGET_VALUE,
            ...value
        })
    );

    const [scheduleHierarchyFormWidgetState, setScheduleHierarchyFormWidgetState] =
        useState<ScheduleHierarchyFormWidgetValue>(
            cloneDeepScheduleHierarchyFormWidgetValue(value as ScheduleHierarchyFormWidgetValue)
        );
    const latestValueRef = useRef<ScheduleFormWidgetValue | ScheduleHierarchyFormWidgetValue>(value);

    const calculateNextScheduleValueAndUpdateState = useCallback(
        async (nextScheduleValue: ScheduleFormWidgetValue | ScheduleHierarchyFormWidgetValue) => {
            let scheduleCalculationError: Error | undefined = undefined;

            if (!loading) {
                onCalculationStateChange?.(true);
            }

            if (isScheduleHierarchyFormWidgetValue(nextScheduleValue)) {
                const { calculatedNextValue, error } = loading
                    ? {
                          calculatedNextValue: cloneDeepScheduleHierarchyFormWidgetValue(nextScheduleValue),
                          error: undefined
                      }
                    : await calculateNextScheduleHierarchyFormWidgetValue(nextScheduleValue, locale, calendarsData);

                scheduleCalculationError = error;
                setScheduleHierarchyFormWidgetState((previousValue) => {
                    if (!isEqual(previousValue, calculatedNextValue)) {
                        return calculatedNextValue;
                    }
                    return previousValue;
                });
            } else {
                const { calculatedNextValue, error } = loading
                    ? { calculatedNextValue: cloneDeep(nextScheduleValue), error: undefined }
                    : await calculateNextStandAloneScheduleFormWidgetValue(nextScheduleValue, locale, calendarsData);

                scheduleCalculationError = error;

                setScheduleFormWidgetState((previousValue) => {
                    if (!isScheduleValueEqual(previousValue, calculatedNextValue)) {
                        return calculatedNextValue;
                    }
                    return previousValue;
                });
            }

            onCalculationStateChange?.(false);

            if (scheduleCalculationError) {
                console.error('Schedule calculation failed:', scheduleCalculationError);
                failedToastMessage('Schedule calculation failed. Please try again.');
            }
        },
        [calendarsData, loading, locale, onCalculationStateChange]
    );

    /**
     * Update internal calculated state on value prop change.
     */
    useEffect(() => {
        latestValueRef.current = value;
        calculateNextScheduleValueAndUpdateState(value);
    }, [calculateNextScheduleValueAndUpdateState, value]);

    /**
     * Call onChange prop on internal calculated state change. Do it conditionally to avoid infinite loops.
     * Read the latest external value from a ref so async prop updates do not trigger a stale internal value
     * to be emitted back to the parent before recalculation catches up.
     */
    useEffect(() => {
        const latestValue = latestValueRef.current;

        if (
            mode === 'stand-alone-schedule' &&
            !isScheduleHierarchyFormWidgetValue(latestValue) &&
            !isScheduleValueEqual(scheduleFormWidgetState, latestValue)
        ) {
            onChange(scheduleFormWidgetState);
        } else if (
            mode === 'hierarchy-schedule' &&
            isScheduleHierarchyFormWidgetValue(latestValue) &&
            !isEqual(scheduleHierarchyFormWidgetState, latestValue)
        ) {
            onChange(scheduleHierarchyFormWidgetState);
        }
    }, [mode, onChange, scheduleFormWidgetState, scheduleHierarchyFormWidgetState]);

    // Manage FormProvider state ---------------------------------------------------------------------------------------

    /**
     * FormProvider is used here as the orchestrator for form widget values changes and associated validation.
     * It's state is a clone of the internal calculated state.
     * It's change handler is a debounced version of the internal calculated state update function.
     */

    const formState = useMemo(() => {
        const cloned =
            mode === 'stand-alone-schedule'
                ? cloneDeep(scheduleFormWidgetState)
                : cloneDeepScheduleHierarchyFormWidgetValue(scheduleHierarchyFormWidgetState);

        return cloned;
    }, [mode, scheduleFormWidgetState, scheduleHierarchyFormWidgetState]);

    const handleFormStateChange = useMemo(
        () => debounce(calculateNextScheduleValueAndUpdateState, SCHEDULE_CALCULATION_DEBOUNCE_MS),
        [calculateNextScheduleValueAndUpdateState]
    );

    /**
     * Cleanup debounced handler on unmount
     */
    useEffect(() => {
        return () => {
            handleFormStateChange.cancel();
        };
    }, [handleFormStateChange]);

    // Prepare context value -------------------------------------------------------------------------------------------

    const contextValue = useMemo(() => {
        const commonValue: ScheduleFormWidgetCommonContextValue = {
            loading,
            error,
            disabled,
            durationUnitsData,
            calendarsData,
            allowedDurationUnits,
            allowedLocations,
            allowAdditionalLocations,
            allowedCalendars,
            specifyDurationFormFieldLabelToken,
            nameFormFieldLabelToken,
            scheduleHierarchyModeFormFieldLabelToken,
            sequentialFormFieldLabelToken
        };

        let contextValue: ScheduleFormWidgetContextValue;

        if (mode === 'stand-alone-schedule') {
            contextValue = {
                ...commonValue,
                mode,
                getFormStateByFieldName: (fieldName) => get(scheduleFormWidgetState, fieldName)
            };
        } else {
            contextValue = {
                ...commonValue,
                mode,
                getFormStateByFieldName: (fieldName) => get(scheduleHierarchyFormWidgetState, fieldName)
            };
        }

        return contextValue;
    }, [
        allowAdditionalLocations,
        allowedCalendars,
        allowedDurationUnits,
        allowedLocations,
        calendarsData,
        disabled,
        durationUnitsData,
        error,
        loading,
        mode,
        nameFormFieldLabelToken,
        scheduleFormWidgetState,
        scheduleHierarchyFormWidgetState,
        scheduleHierarchyModeFormFieldLabelToken,
        sequentialFormFieldLabelToken,
        specifyDurationFormFieldLabelToken
    ]);

    return (
        /**
         * @todo: Utilize FormContext's error handling instead of passing error down via ScheduleFormWidgetContext
         * Currently, errors are passed down via ScheduleFormWidgetContext to keep changes minimal.
         * A more comprehensive refactor is needed to fully integrate field validation and
         * field level and form level error handling using FormContext.
         */
        <FormProvider state={formState} setState={handleFormStateChange} disableFunctionalStateUpdate>
            <ScheduleFormWidgetContext value={contextValue}>{children}</ScheduleFormWidgetContext>
        </FormProvider>
    );
}

function isScheduleHierarchyFormWidgetValue(
    value: ScheduleFormWidgetValue | ScheduleHierarchyFormWidgetValue
): value is ScheduleHierarchyFormWidgetValue {
    const hasParentProperty = (value as ScheduleHierarchyFormWidgetValue).parent !== undefined;
    const hasChildrenProperty = (value as ScheduleHierarchyFormWidgetValue).children !== undefined;
    const hasUseChildSchedulesProperty = (value as ScheduleHierarchyFormWidgetValue).useChildSchedules !== undefined;
    const hasSequentialProperty = (value as ScheduleHierarchyFormWidgetValue).sequential !== undefined;

    return hasParentProperty && hasChildrenProperty && hasUseChildSchedulesProperty && hasSequentialProperty;
}

/**
 * Somehow lodash's cloneDeep does not work correctly for ScheduleHierarchyFormWidgetValue due to its nested structure.
 * This function ensures a proper deep clone of ScheduleHierarchyFormWidgetValue.
 */
function cloneDeepScheduleHierarchyFormWidgetValue(value: ScheduleHierarchyFormWidgetValue) {
    if ('parent' in value && 'children' in value) {
        return {
            ...value,
            parent: {
                id: value.parent.id,
                value: cloneDeep(value.parent.value)
            },
            children: value.children.map((child) => ({
                id: child.id,
                value: cloneDeep(child.value)
            }))
        };
    }

    return value;
}
